import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Slider } from '@/components/ui/slider';
import { toast } from 'sonner';
import {
  QrCode,
  Download,
  Palette,
  Type,
  Image,
  Link,
  Upload,
  FileText,
  Grid3X3,
  Leaf,
  Crown,
  Zap,
  Briefcase,
  Store,
  Minus,
  RefreshCw,
  Eye,
  FileDown,
  Table,
  Coffee,
  ArrowRight
} from 'lucide-react';
import axios from 'axios';

// Use relative path for API calls (works on any domain)
const API = typeof window !== 'undefined' && window.location.hostname !== 'localhost' 
  ? '/api' 
  : `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_ICONS = {
  all: Grid3X3,
  minimalista: Minus,
  corporativo: Briefcase,
  premium: Crown,
  moderno: Zap,
  colorido: Palette,
  natural: Leaf,
  comercial: Store
};

export default function PosterDesigner({ onClose }) {
  const { token } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [categories, setCategories] = useState({});
  const [suggestedTexts, setSuggestedTexts] = useState([]);
  const [outputSizes, setOutputSizes] = useState({});
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  
  // Estado del diseñador
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  
  // Configuración del cartel
  const [config, setConfig] = useState({
    qrType: 'unit', // 'unit', 'custom_url', 'upload'
    unitId: '',
    customUrl: '',
    qrImageBase64: null,
    titleText: 'ESCANEÁ EL QR',
    subtitleText: '',
    businessName: '',
    logoBase64: null,
    titleColor: '#1F2937',
    titleSize: 24,
    fontFamily: 'Arial',
    outputFormat: 'png',
    outputSize: 'A5'
  });

  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [templatesRes, categoriesRes, textsRes, sizesRes, unitsRes] = await Promise.all([
        axios.get(`${API}/poster-designer/templates`, { headers }),
        axios.get(`${API}/poster-designer/categories`, { headers }),
        axios.get(`${API}/poster-designer/suggested-texts`, { headers }),
        axios.get(`${API}/poster-designer/output-sizes`, { headers }),
        axios.get(`${API}/poster-designer/units`, { headers })
      ]);
      
      setTemplates(templatesRes.data.templates || []);
      setCategories(categoriesRes.data.categories || {});
      setSuggestedTexts(textsRes.data.texts || []);
      setOutputSizes(sizesRes.data.sizes || {});
      setUnits(unitsRes.data.units || []);
      
      // Seleccionar primera plantilla por defecto
      if (templatesRes.data.templates?.length > 0) {
        setSelectedTemplate(templatesRes.data.templates[0]);
      }
    } catch (err) {
      console.error('Error fetching data:', err);
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Actualizar preview cuando cambia la configuración
  useEffect(() => {
    if (selectedTemplate && !loading) {
      generatePreview();
    }
  }, [selectedTemplate, config.qrType, config.unitId, config.customUrl, config.titleText, config.businessName, config.titleColor]);

  const generatePreview = async () => {
    if (!selectedTemplate) return;
    
    try {
      setPreviewLoading(true);
      const res = await axios.post(`${API}/poster-designer/preview`, {
        template_id: selectedTemplate.template_id,
        qr_type: config.qrType,
        unit_id: config.qrType === 'unit' ? config.unitId : null,
        custom_url: config.qrType === 'custom_url' ? config.customUrl : null,
        qr_image_base64: config.qrType === 'upload' ? config.qrImageBase64 : null,
        title_text: config.titleText,
        subtitle_text: config.subtitleText,
        business_name: config.businessName,
        logo_base64: config.logoBase64,
        title_color: config.titleColor,
        title_size: config.titleSize,
        font_family: config.fontFamily,
        output_format: config.outputFormat,
        output_size: config.outputSize
      }, { headers });
      
      setPreviewImage(res.data.preview_base64);
    } catch (err) {
      console.error('Error generating preview:', err);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleGenerate = async () => {
    if (!selectedTemplate) {
      toast.error('Selecciona una plantilla');
      return;
    }
    
    if (config.qrType === 'unit' && !config.unitId) {
      toast.error('Selecciona una mesa/unidad');
      return;
    }
    
    if (config.qrType === 'custom_url' && !config.customUrl) {
      toast.error('Ingresa una URL');
      return;
    }
    
    try {
      setGenerating(true);
      const res = await axios.post(`${API}/poster-designer/generate`, {
        template_id: selectedTemplate.template_id,
        qr_type: config.qrType,
        unit_id: config.qrType === 'unit' ? config.unitId : null,
        custom_url: config.qrType === 'custom_url' ? config.customUrl : null,
        qr_image_base64: config.qrType === 'upload' ? config.qrImageBase64 : null,
        title_text: config.titleText,
        subtitle_text: config.subtitleText,
        business_name: config.businessName,
        logo_base64: config.logoBase64,
        title_color: config.titleColor,
        title_size: config.titleSize,
        font_family: config.fontFamily,
        output_format: config.outputFormat,
        output_size: config.outputSize
      }, { 
        headers,
        responseType: 'blob'
      });
      
      // Descargar archivo
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      const extension = config.outputFormat === 'pdf' ? 'pdf' : 'png';
      link.setAttribute('download', `cartel_qr_${selectedTemplate.name.replace(/\s+/g, '_')}.${extension}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(`Cartel descargado en formato ${config.outputFormat.toUpperCase()}`);
    } catch (err) {
      toast.error('Error al generar el cartel');
    } finally {
      setGenerating(false);
    }
  };

  const handleFileUpload = (e, type) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = (event) => {
      const base64 = event.target?.result;
      if (type === 'qr') {
        setConfig(c => ({ ...c, qrImageBase64: base64 }));
      } else if (type === 'logo') {
        setConfig(c => ({ ...c, logoBase64: base64 }));
      }
    };
    reader.readAsDataURL(file);
  };

  const filteredTemplates = selectedCategory === 'all' 
    ? templates 
    : templates.filter(t => t.category === selectedCategory);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="poster-designer">
      <div className="grid lg:grid-cols-12 gap-6">
        {/* Panel izquierdo: Galería de plantillas */}
        <div className="lg:col-span-3 space-y-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Grid3X3 className="w-4 h-4" />
                Plantillas
              </CardTitle>
              <CardDescription>
                {filteredTemplates.length} diseños disponibles
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {/* Filtro por categoría */}
              <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                <SelectTrigger className="w-full" data-testid="category-filter">
                  <SelectValue placeholder="Categoría" />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(categories).map(([key, cat]) => {
                    const Icon = CATEGORY_ICONS[key] || Grid3X3;
                    return (
                      <SelectItem key={key} value={key}>
                        <div className="flex items-center gap-2">
                          <Icon className="w-4 h-4" />
                          {cat.name}
                        </div>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
              
              {/* Grid de plantillas */}
              <ScrollArea className="h-[400px]">
                <div className="grid grid-cols-2 gap-2 pr-4">
                  {filteredTemplates.map(template => (
                    <button
                      key={template.template_id}
                      onClick={() => setSelectedTemplate(template)}
                      className={`group relative aspect-[4/5] rounded-lg border-2 overflow-hidden transition-all ${
                        selectedTemplate?.template_id === template.template_id
                          ? 'border-primary ring-2 ring-primary ring-offset-2'
                          : 'border-border hover:border-primary/50'
                      }`}
                      data-testid={`template-${template.template_id}`}
                    >
                      {/* Preview de la plantilla usando colores */}
                      <div 
                        className="absolute inset-0 flex items-center justify-center"
                        style={{ 
                          background: template.colors?.[0] ? 
                            `linear-gradient(135deg, ${template.colors[0]} 0%, ${template.colors[1] || template.colors[0]} 100%)` 
                            : '#f5f5f5'
                        }}
                      >
                        <div className="w-8 h-8 bg-white rounded flex items-center justify-center shadow-md">
                          <QrCode className="w-5 h-5 text-gray-600" />
                        </div>
                      </div>
                      
                      {/* Nombre */}
                      <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-xs p-1 text-center truncate">
                        {template.name}
                      </div>
                      
                      {/* Badge de categoría */}
                      <Badge 
                        variant="secondary" 
                        className="absolute top-1 right-1 text-[10px] px-1 py-0"
                      >
                        {categories[template.category]?.name || template.category}
                      </Badge>
                    </button>
                  ))}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        </div>

        {/* Panel central: Vista previa */}
        <div className="lg:col-span-5">
          <Card className="h-full">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Eye className="w-4 h-4" />
                Vista Previa
              </CardTitle>
              <CardDescription>
                {selectedTemplate?.name || 'Selecciona una plantilla'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-center min-h-[450px] bg-muted/30 rounded-lg p-4">
                {previewLoading ? (
                  <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
                ) : previewImage ? (
                  <img 
                    src={previewImage} 
                    alt="Vista previa del cartel"
                    className="max-w-full max-h-[420px] rounded-lg shadow-lg"
                    data-testid="poster-preview-image"
                  />
                ) : (
                  <div className="text-center text-muted-foreground">
                    <QrCode className="w-16 h-16 mx-auto mb-4 opacity-30" />
                    <p>Selecciona una plantilla y configura tu QR</p>
                  </div>
                )}
              </div>
              
              {/* Botón de refresh */}
              <div className="flex justify-center mt-4">
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={generatePreview}
                  disabled={!selectedTemplate || previewLoading}
                >
                  <RefreshCw className={`w-4 h-4 mr-2 ${previewLoading ? 'animate-spin' : ''}`} />
                  Actualizar Vista Previa
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Panel derecho: Opciones de personalización */}
        <div className="lg:col-span-4 space-y-4">
          {/* Fuente del QR */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <QrCode className="w-4 h-4" />
                Código QR
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <RadioGroup 
                value={config.qrType} 
                onValueChange={(v) => setConfig(c => ({ ...c, qrType: v }))}
                className="space-y-2"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="unit" id="qr-unit" />
                  <Label htmlFor="qr-unit" className="flex items-center gap-2 cursor-pointer">
                    <Table className="w-4 h-4" />
                    Mesa del negocio
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="custom_url" id="qr-url" />
                  <Label htmlFor="qr-url" className="flex items-center gap-2 cursor-pointer">
                    <Link className="w-4 h-4" />
                    URL personalizada
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="upload" id="qr-upload" />
                  <Label htmlFor="qr-upload" className="flex items-center gap-2 cursor-pointer">
                    <Upload className="w-4 h-4" />
                    Subir imagen QR
                  </Label>
                </div>
              </RadioGroup>
              
              {config.qrType === 'unit' && (
                <Select 
                  value={config.unitId} 
                  onValueChange={(v) => setConfig(c => ({ ...c, unitId: v }))}
                >
                  <SelectTrigger data-testid="unit-select">
                    <SelectValue placeholder="Selecciona una mesa" />
                  </SelectTrigger>
                  <SelectContent>
                    {units.map(unit => (
                      <SelectItem key={unit.unit_id} value={unit.unit_id}>
                        {unit.name} - {unit.space_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              
              {config.qrType === 'custom_url' && (
                <Input
                  placeholder="https://example.com/pago"
                  value={config.customUrl}
                  onChange={(e) => setConfig(c => ({ ...c, customUrl: e.target.value }))}
                  data-testid="custom-url-input"
                />
              )}
              
              {config.qrType === 'upload' && (
                <div>
                  <Input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleFileUpload(e, 'qr')}
                    className="cursor-pointer"
                  />
                  {config.qrImageBase64 && (
                    <div className="mt-2 flex items-center gap-2 text-sm text-green-600">
                      <QrCode className="w-4 h-4" />
                      Imagen QR cargada
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Textos */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Type className="w-4 h-4" />
                Textos
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Título principal</Label>
                <Select 
                  value={config.titleText}
                  onValueChange={(v) => setConfig(c => ({ ...c, titleText: v }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecciona o escribe" />
                  </SelectTrigger>
                  <SelectContent>
                    {suggestedTexts.map(text => (
                      <SelectItem key={text} value={text}>{text}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input
                  placeholder="O escribe tu texto personalizado"
                  value={config.titleText}
                  onChange={(e) => setConfig(c => ({ ...c, titleText: e.target.value }))}
                  data-testid="title-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Nombre del negocio (opcional)</Label>
                <Input
                  placeholder="Mi Restaurante"
                  value={config.businessName}
                  onChange={(e) => setConfig(c => ({ ...c, businessName: e.target.value }))}
                />
              </div>
              
              <div className="space-y-2">
                <Label>Color del texto</Label>
                <div className="flex gap-2">
                  <input
                    type="color"
                    value={config.titleColor}
                    onChange={(e) => setConfig(c => ({ ...c, titleColor: e.target.value }))}
                    className="w-12 h-10 p-0.5 rounded cursor-pointer border bg-transparent [&::-webkit-color-swatch-wrapper]:p-0 [&::-webkit-color-swatch]:border-0 [&::-webkit-color-swatch]:rounded"
                  />
                  <Input
                    value={config.titleColor}
                    onChange={(e) => setConfig(c => ({ ...c, titleColor: e.target.value }))}
                    className="flex-1 font-mono"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Logo */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Image className="w-4 h-4" />
                Logo (opcional)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Input
                type="file"
                accept="image/*"
                onChange={(e) => handleFileUpload(e, 'logo')}
                className="cursor-pointer"
              />
              {config.logoBase64 && (
                <div className="mt-2 flex items-center gap-2">
                  <img 
                    src={config.logoBase64} 
                    alt="Logo"
                    className="w-10 h-10 object-contain rounded border"
                  />
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => setConfig(c => ({ ...c, logoBase64: null }))}
                  >
                    Quitar
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Exportación */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <FileDown className="w-4 h-4" />
                Exportar
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Formato</Label>
                  <Select 
                    value={config.outputFormat}
                    onValueChange={(v) => setConfig(c => ({ ...c, outputFormat: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="png">PNG (imagen)</SelectItem>
                      <SelectItem value="pdf">PDF (impresión)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Tamaño</Label>
                  <Select 
                    value={config.outputSize}
                    onValueChange={(v) => setConfig(c => ({ ...c, outputSize: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(outputSizes).map(([key, size]) => (
                        <SelectItem key={key} value={key}>{size.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <Button 
                className="w-full" 
                onClick={handleGenerate}
                disabled={generating || !selectedTemplate}
                data-testid="generate-poster-btn"
              >
                {generating ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                ) : (
                  <Download className="w-4 h-4 mr-2" />
                )}
                Descargar Cartel
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
